<script setup lang="ts">
import type { CalendarEvent, EnumResponseType, GaiaJsonValue } from '@/api';
import {
  RECURRENCE_FREQUENCY_LABELS,
  RECURRENCE_TYPE_LABELS,
  readRecurrence,
  type DateRecurrence,
} from '@/services/contractManagement/dateRow';
import type { FieldType } from '@/types/evaluation';
import { getCurrencyOptions } from '@/utils/currency';
import { getFieldCategory, getListItemType } from '@/utils/fieldType';
import { safeStringify } from '@/utils/jsonHelpers';
import { gaiaNeutral } from '../../themes/tokens';

import { computed, ref, watch } from 'vue';
import GaiaAddButton from '../../base/GaiaAddButton.vue';
import GaiaDatePicker from '../../base/inputs/GaiaDatePicker.vue';
import GaiaDeleteButton from '../../base/GaiaDeleteButton.vue';
import GaiaFormField from '../../base/GaiaFormField.vue';
import GaiaInput from '../../base/inputs/GaiaInput.vue';
import GaiaInputNumber from '../../base/inputs/GaiaInputNumber.vue';
import GaiaSelect from '../../base/selects/GaiaSelect.vue';
import GaiaText from '../../base/GaiaText.vue';

interface Props {
  fieldType: FieldType;
  value: GaiaJsonValue;
  enumOptions?: EnumResponseType;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:value': [value: GaiaJsonValue];
  save: [];
  cancel: [];
}>();

// Local state for different field types
const textValue = ref<string>('');
const booleanValue = ref<string | null>(null);
const choiceValue = ref<string | null>(null);
const multipleChoiceValue = ref<string[]>([]);
const dateValue = ref<number | null>(null);
const numberValue = ref<number | null>(null);
const currencyAmount = ref<number | null>(null);
const currencyCode = ref<string | null>(null);
const entityName = ref<string>('');
const entityAddress = ref<string>('');
// Calendar events list for multiple cycles. Mirrors `CalendarEvent`, with
// dates as the pickers' timestamps.
interface CalendarEventItem {
  title: string;
  date: number | null;
  type: DateRecurrence['recurrence_type'];
  frequency: DateRecurrence['recurrence_frequency'];
  interval: number | null;
  count: number | null;
  untilDate: number | null;
}

const calendarEvents = ref<CalendarEventItem[]>([]);
const listItems = ref<GaiaJsonValue[]>([]);

// Boolean options
const booleanOptions = [
  { label: 'Yes', value: 'true', type: 'option' as const },
  { label: 'No', value: 'false', type: 'option' as const },
];

// Currency options
const currencyOptions = getCurrencyOptions();

// Recurrence options for calendar events
const toOptions = (
  labels: Record<string, string>
): { label: string; value: string; type: 'option' }[] =>
  Object.entries(labels).map(([value, label]) => ({ label, value, type: 'option' as const }));
const frequencyOptions = toOptions(RECURRENCE_FREQUENCY_LABELS);
const repeatTypeOptions = toOptions(RECURRENCE_TYPE_LABELS);

function emptyCalendarEvent(): CalendarEventItem {
  return {
    title: '',
    date: null,
    type: 'never',
    frequency: null,
    interval: null,
    count: null,
    untilDate: null,
  };
}

function toTimestamp(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null;
  const time = new Date(value).getTime();
  return isNaN(time) ? null : time;
}

// Use local date string to avoid timezone issues
const toDateString = (time: number): string => new Date(time).toLocaleDateString('en-CA');

/** A stored event as the editor's item; a draft may still lack its date. */
function toCalendarEventItem(item: unknown): CalendarEventItem {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return emptyCalendarEvent();
  const event = item as Record<string, unknown>;
  const recurrence = readRecurrence(event);
  return {
    title: typeof event.title === 'string' ? event.title : '',
    date: toTimestamp(event.event_date),
    type: recurrence.recurrence_type,
    frequency: recurrence.recurrence_frequency ?? null,
    interval: recurrence.recurrence_interval ?? null,
    count: recurrence.recurrence_count ?? null,
    untilDate: toTimestamp(recurrence.recurrence_until_date),
  };
}

// Computed properties for field type detection
const fieldCategory = computed(() => getFieldCategory(props.fieldType));
const listItemType = computed(() => getListItemType(props.fieldType));
const isDateOnly = computed(() => props.fieldType === 'date' || props.fieldType === 'list[date]');

// Choice options for enum types
const choiceOptions = computed(() => {
  if (!props.enumOptions || typeof props.fieldType !== 'object') return [];

  return props.enumOptions.values.map(option => ({
    label: option.name,
    value: option.name,
    type: 'option' as const,
  }));
});

// Initialize values based on field type
watch(
  () => props.value,
  newValue => {
    const category = fieldCategory.value;

    switch (category) {
      case 'boolean':
        booleanValue.value = typeof newValue === 'boolean' ? String(newValue) : null;
        break;
      case 'enum-single':
        choiceValue.value = typeof newValue === 'string' ? newValue : null;
        break;
      case 'enum-multiple':
        multipleChoiceValue.value = Array.isArray(newValue) ? (newValue as string[]) : [];
        break;
      case 'datetime':
        if (typeof newValue === 'number') {
          dateValue.value = newValue;
        } else if (typeof newValue === 'string' && newValue) {
          dateValue.value = new Date(newValue).getTime();
        } else {
          dateValue.value = null;
        }
        break;
      case 'number':
        numberValue.value = typeof newValue === 'number' ? newValue : null;
        break;
      case 'currency':
        if (typeof newValue === 'object' && newValue !== null) {
          const currencyObj = newValue as Record<string, unknown>;
          currencyAmount.value = typeof currencyObj.amount === 'number' ? currencyObj.amount : null;
          currencyCode.value =
            typeof currencyObj.iso_4217_alpha_3 === 'string' ? currencyObj.iso_4217_alpha_3 : null;
        } else {
          currencyAmount.value = null;
          currencyCode.value = null;
        }
        break;
      case 'entity':
        if (typeof newValue === 'object' && newValue !== null) {
          const entityObj = newValue as Record<string, unknown>;
          entityName.value = typeof entityObj.name === 'string' ? entityObj.name : '';
          entityAddress.value = typeof entityObj.address === 'string' ? entityObj.address : '';
        }
        break;
      case 'calendar-event':
        // A single event (older values) reads as a list of one.
        if (Array.isArray(newValue)) {
          calendarEvents.value = newValue.map(toCalendarEventItem);
        } else if (typeof newValue === 'object' && newValue !== null) {
          calendarEvents.value = [toCalendarEventItem(newValue)];
        } else {
          calendarEvents.value = [emptyCalendarEvent()];
        }
        break;
      case 'list':
        if (Array.isArray(newValue)) {
          listItems.value = [...newValue];
        } else {
          listItems.value = [];
        }
        break;
      default:
        textValue.value = newValue ? safeStringify(newValue) : '';
    }
  },
  { immediate: true }
);

// Event handlers
function handleTextChange(value: string): void {
  textValue.value = value;
  emit('update:value', value);
}

function handleBooleanChange(value: string | null): void {
  booleanValue.value = value;
  emit('update:value', value === 'true');
}

function handleChoiceChange(value: string | null): void {
  choiceValue.value = value;
  emit('update:value', value ?? '');
}

function handleMultipleChoiceChange(value: string[]): void {
  multipleChoiceValue.value = value;
  emit('update:value', value);
}

function handleDateChange(value: number | null): void {
  dateValue.value = value;
  emit('update:value', value);
}

function handleNumberChange(value: number | null): void {
  numberValue.value = value;
  emit('update:value', value as GaiaJsonValue);
}

function handleCurrencyChange(): void {
  const currencyValue = {
    amount: currencyAmount.value,
    iso_4217_alpha_3: currencyCode.value,
  };
  emit('update:value', currencyValue);
}

function handleEntityChange(): void {
  const entityValue = {
    name: entityName.value,
    address: entityAddress.value,
  };
  emit('update:value', entityValue);
}

/**
 * Every event in full: the recurrence type is always set, and parts it doesn't
 * use are sent as null, so switching type clears them.
 */
function handleCalendarEventChange(): void {
  const calendarEventsValue = calendarEvents.value.map((event): CalendarEvent => {
    const repeats = event.type !== 'never';
    return {
      title: event.title.trim() || null,
      event_date: event.date ? toDateString(event.date) : '',
      recurrence_type: event.type,
      recurrence_frequency: repeats ? event.frequency : null,
      recurrence_interval: repeats ? event.interval : null,
      recurrence_count: event.type === 'count' ? event.count : null,
      recurrence_until_date:
        event.type === 'until_date' && event.untilDate ? toDateString(event.untilDate) : null,
    };
  });

  emit('update:value', calendarEventsValue);
}

function updateListItem(index: number, value: GaiaJsonValue): void {
  listItems.value[index] = value;
  emit('update:value', [...listItems.value]);
}

function addListItem(): void {
  const defaultValue = listItemType.value === 'str' ? '' : '';
  listItems.value.push(defaultValue);
  emit('update:value', [...listItems.value]);
}

function removeListItem(index: number): void {
  listItems.value.splice(index, 1);
  emit('update:value', [...listItems.value]);
}

function addCalendarEvent(): void {
  calendarEvents.value.push(emptyCalendarEvent());
  handleCalendarEventChange();
}

function removeCalendarEvent(index: number): void {
  calendarEvents.value.splice(index, 1);
  handleCalendarEventChange();
}

const eventBorder = gaiaNeutral.borderCard;
const eventSurface = gaiaNeutral.surfaceSoft;
</script>

<template>
  <!-- Type-aware editor for one extracted value. The category comes from the
       prompt's declared response type (utils/fieldType), never from sniffing
       the value: an empty string is a blank str, not an absent date. Enter
       saves and Escape cancels on every control, so a row can be corrected
       without reaching for the mouse. -->
  <n-flex vertical :size="0" :wrap="false" :style="{ width: '100%' }">
    <!-- Boolean -->
    <gaia-select
      v-if="fieldCategory === 'boolean'"
      v-model:value="booleanValue"
      :options="booleanOptions"
      placeholder="Select Yes or No"
      @update:value="handleBooleanChange"
      @keydown.enter.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    />

    <!-- Choice, single -->
    <gaia-select
      v-else-if="fieldCategory === 'enum-single'"
      v-model:value="choiceValue"
      :options="choiceOptions"
      placeholder="Select an option"
      @update:value="handleChoiceChange"
      @keydown.enter.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    />

    <!-- Choice, multiple -->
    <gaia-select
      v-else-if="fieldCategory === 'enum-multiple'"
      v-model:value="multipleChoiceValue"
      :options="choiceOptions"
      multiple
      placeholder="Select one or more options"
      @update:value="handleMultipleChoiceChange"
      @keydown.enter.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    />

    <!-- Date / date & time -->
    <gaia-date-picker
      v-else-if="fieldCategory === 'datetime'"
      v-model:value="dateValue"
      :type="isDateOnly ? 'date' : 'datetime'"
      :placeholder="isDateOnly ? 'Select date' : 'Select date and time'"
      @update:value="handleDateChange"
      @keydown.enter.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    />

    <!-- Number -->
    <gaia-input-number
      v-else-if="fieldCategory === 'number'"
      v-model:value="numberValue"
      placeholder="Enter a number"
      :precision="fieldType === 'int' ? 0 : undefined"
      clearable
      @update:value="handleNumberChange"
      @keydown.enter.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    />

    <!-- Currency: amount + ISO 4217 code -->
    <n-flex v-else-if="fieldCategory === 'currency'" align="center" :size="8" :wrap="false">
      <gaia-input-number
        v-model:value="currencyAmount"
        placeholder="Amount"
        :precision="2"
        clearable
        :style="{ flex: 1, minWidth: 0 }"
        @update:value="handleCurrencyChange"
        @keydown.enter.prevent="$emit('save')"
        @keydown.escape="$emit('cancel')"
      />
      <gaia-select
        v-model:value="currencyCode"
        :options="currencyOptions"
        placeholder="Currency"
        :style="{ width: '120px', flex: 'none' }"
        @update:value="handleCurrencyChange"
        @keydown.enter.prevent="$emit('save')"
        @keydown.escape="$emit('cancel')"
      />
    </n-flex>

    <!-- Entity -->
    <n-flex v-else-if="fieldCategory === 'entity'" vertical :size="8" :wrap="false">
      <gaia-input
        v-model:value="entityName"
        placeholder="Name"
        @update:value="handleEntityChange"
        @keydown.enter.prevent="$emit('save')"
        @keydown.escape="$emit('cancel')"
      />
      <gaia-input
        v-model:value="entityAddress"
        placeholder="Address"
        @update:value="handleEntityChange"
        @keydown.enter.prevent="$emit('save')"
        @keydown.escape="$emit('cancel')"
      />
    </n-flex>

    <!-- Calendar events: a list of cycles, because one obligation can recur on
         several different schedules (e.g. a termination date per year). -->
    <n-flex v-else-if="fieldCategory === 'calendar-event'" vertical :size="16" :wrap="false">
      <n-flex vertical :size="4" :wrap="false">
        <gaia-text size="row" weight="semibold" tone="ink">Multiple cycles / date series</gaia-text>
        <gaia-text size="meta" tone="subtle">
          Add several calendar events to cover varying schedules.
        </gaia-text>
      </n-flex>

      <n-flex
        v-for="(event, index) in calendarEvents"
        :key="index"
        vertical
        :size="12"
        :wrap="false"
        class="event"
      >
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-text size="row" weight="semibold" tone="ink" :style="{ flex: 1, minWidth: 0 }">
            Event {{ index + 1 }}
          </gaia-text>
          <gaia-delete-button
            intent="remove"
            variant="label"
            size="xs"
            @click="removeCalendarEvent(index)"
          />
        </n-flex>

        <gaia-form-field label="Title">
          <gaia-input
            v-model:value="event.title"
            placeholder="e.g. Termination notice deadline"
            @update:value="handleCalendarEventChange"
            @keydown.enter.prevent="$emit('save')"
            @keydown.escape="$emit('cancel')"
          />
        </gaia-form-field>
        <gaia-form-field label="Event date">
          <gaia-date-picker
            v-model:value="event.date"
            placeholder="Event date"
            @update:value="handleCalendarEventChange"
            @keydown.enter.prevent="$emit('save')"
            @keydown.escape="$emit('cancel')"
          />
        </gaia-form-field>
        <gaia-form-field label="Repeats">
          <gaia-select
            v-model:value="event.type"
            :options="repeatTypeOptions"
            @update:value="handleCalendarEventChange"
            @keydown.enter.prevent="$emit('save')"
            @keydown.escape="$emit('cancel')"
          />
        </gaia-form-field>
        <template v-if="event.type !== 'never'">
          <gaia-form-field label="Every">
            <gaia-input-number
              v-model:value="event.interval"
              placeholder="1"
              :min="1"
              clearable
              @update:value="handleCalendarEventChange"
              @keydown.enter.prevent="$emit('save')"
              @keydown.escape="$emit('cancel')"
            />
          </gaia-form-field>
          <gaia-form-field label="Frequency">
            <gaia-select
              v-model:value="event.frequency"
              :options="frequencyOptions"
              placeholder="Frequency"
              @update:value="handleCalendarEventChange"
              @keydown.enter.prevent="$emit('save')"
              @keydown.escape="$emit('cancel')"
            />
          </gaia-form-field>
        </template>
        <gaia-form-field v-if="event.type === 'count'" label="Occurrences">
          <gaia-input-number
            v-model:value="event.count"
            placeholder="Count"
            :min="1"
            clearable
            @update:value="handleCalendarEventChange"
            @keydown.enter.prevent="$emit('save')"
            @keydown.escape="$emit('cancel')"
          />
        </gaia-form-field>
        <gaia-form-field v-if="event.type === 'until_date'" label="Until">
          <gaia-date-picker
            v-model:value="event.untilDate"
            placeholder="Until date"
            @update:value="handleCalendarEventChange"
            @keydown.enter.prevent="$emit('save')"
            @keydown.escape="$emit('cancel')"
          />
        </gaia-form-field>
      </n-flex>

      <gaia-add-button
        subject="calendar event"
        :style="{ alignSelf: 'flex-start' }"
        @click="addCalendarEvent"
      />
    </n-flex>

    <!-- List: one nested editor of the item type per entry. -->
    <n-flex v-else-if="fieldCategory === 'list'" vertical :size="8" :wrap="false">
      <n-flex
        v-for="(item, index) in listItems"
        :key="index"
        align="flex-start"
        :size="8"
        :wrap="false"
      >
        <gaia-typed-value-editor
          :field-type="listItemType"
          :value="item"
          :enum-options="enumOptions"
          :style="{ flex: 1, minWidth: 0 }"
          @update:value="newValue => updateListItem(index, newValue)"
        />
        <gaia-delete-button
          intent="remove"
          variant="label"
          :style="{ flex: 'none' }"
          @click="removeListItem(index)"
        />
      </n-flex>

      <gaia-add-button subject="item" :style="{ alignSelf: 'flex-start' }" @click="addListItem" />
    </n-flex>

    <!-- Anything else edits as text. -->
    <gaia-input
      v-else
      v-model:value="textValue"
      type="textarea"
      :autosize="{ minRows: 2, maxRows: 6 }"
      placeholder="Enter value"
      @update:value="handleTextChange"
      @keydown.enter.prevent="$emit('save')"
      @keydown.escape="$emit('cancel')"
    />
  </n-flex>
</template>

<style scoped>
/* One card per cycle so a multi-event schedule stays readable. The rule is
   selector-bound (it styles a v-for'd box, not a run of copy), so it reads its
   colours through v-bind rather than living inline. */
.event {
  padding: 12px;
  border: 1px solid v-bind(eventBorder);
  border-radius: 10px;
  background: v-bind(eventSurface);
}
</style>
